/**
 * The aggregated `property_list` fields of a multi-property template
 * (e.g. Fastighetsbeteckningar / Samtliga adresser), each with an
 * Automatisk / Manuell switch. Automatic shows the text generated from the
 * linked properties; manual turns it into a free-text field, pre-filled with
 * the generated text. Switching back to automatic regenerates it.
 */

import { useState } from 'react';
import {
  manualPropertyListKeys,
  type VariableField,
} from '@hyranai/regta-templates';
import { tenantApi } from '../api.js';
import { AutofillModeField, type AutofillMode as Mode } from './autofill/AutofillModeField.js';

export function PropertyListFields({
  contractId,
  fields,
  values,
  disabled,
  modeLocked,
  onTextChange,
  onModeChanged,
}: {
  contractId: string;
  fields: ReadonlyArray<VariableField<Record<string, unknown>>>;
  values: Record<string, unknown>;
  disabled: boolean;
  /** Switching mode is draft-only server-side; lock it outside draft. */
  modeLocked: boolean;
  onTextChange: (key: string, next: string) => void;
  /** Server-confirmed values after a switch; merged into the editor as clean. */
  onModeChanged: (patch: Record<string, unknown>) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const manual = manualPropertyListKeys(values);

  if (fields.length === 0) return null;

  const switchMode = async (key: string, mode: Mode) => {
    setBusyKey(key);
    setErr(null);
    try {
      const r = await tenantApi.contract_properties.set_mode({
        contract_id: contractId,
        field_key: key,
        mode,
      });
      onModeChanged(r.values);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-stone-100 pt-3">
      {fields.map((f) => {
        const mode: Mode = manual.has(f.key) ? 'manual' : 'auto';
        const text = String(values[f.key] ?? '');
        const busy = busyKey === f.key;
        return (
          <AutofillModeField
            key={f.key}
            id={`plf-${f.key}`}
            label={f.label}
            mode={mode}
            text={text}
            disabled={disabled}
            modeLocked={modeLocked}
            busy={busy}
            emptyAutoText="Fylls i från länkade fastigheter."
            autoHelp="Hämtas automatiskt från länkade fastigheter."
            manualHelp="Fritext — påverkas inte när fastigheter läggs till eller tas bort."
            onSwitch={(m) => void switchMode(f.key, m)}
            onTextChange={(next) => onTextChange(f.key, next)}
          />
        );
      })}
      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {err}
        </p>
      )}
    </div>
  );
}
